import React, { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Check, X, Loader2, IndianRupee, TrendingUp, AlertTriangle, Zap } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { PageHeader } from "@/components/common";

const fmtINR = (n) => `₹${(n || 0).toLocaleString("en-IN")}`;
const FEATURES = [["inbound", "Inbound calling"], ["whatsapp", "WhatsApp"], ["crm", "CRM sync"], ["ab_testing", "A/B testing"], ["dedicated_support", "Priority support"]];

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true); s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

function UsageBar({ label, used, cap, pct }) {
  const unlimited = !cap;
  const color = pct >= 100 ? "#DC2626" : pct >= 80 ? "#F97316" : "#4f46e5";
  return (
    <div data-testid={`usage-${label.toLowerCase()}`}>
      <div className="flex justify-between text-sm mb-1"><span className="text-slate-500">{label}</span><span className="font-medium">{used.toLocaleString("en-IN")} {unlimited ? "" : `/ ${cap.toLocaleString("en-IN")}`}</span></div>
      <div className="h-2 rounded-full bg-slate-100 dark:bg-white/10"><div className="h-2 rounded-full transition-all" style={{ width: unlimited ? "8%" : `${Math.min(100, pct)}%`, background: color }} /></div>
    </div>
  );
}

export default function Billing() {
  const [d, setD] = useState(null);
  const [paying, setPaying] = useState(null);
  const [buyingOverage, setBuyingOverage] = useState(false);
  const [upgradePlan, setUpgradePlan] = useState(null);
  const load = () => api.get("/billing").then((r) => setD(r.data)).catch(() => setD(false));
  useEffect(() => { load(); }, []);

  const upgrade = async (plan) => {
    if (!d.razorpay_configured) { toast.error("Online payments aren't enabled yet. Please contact PK AI to upgrade."); return; }
    setPaying(plan.id);
    try {
      const ok = await loadRazorpay();
      if (!ok) throw new Error("Could not load payment gateway");
      const { data: order } = await api.post("/billing/create-order", { plan_id: plan.id });
      const rz = new window.Razorpay({
        key: order.key_id, amount: order.amount, currency: order.currency, name: "PK AI Calling",
        description: `${order.plan_name} plan`, order_id: order.order_id,
        handler: async (resp) => {
          try { await api.post("/billing/verify", { ...resp, plan_id: plan.id }); toast.success(`Upgraded to ${plan.name}!`); setUpgradePlan(null); load(); }
          catch (e) { toast.error(apiErr(e)); }
        },
        theme: { color: "#4f46e5" },
        modal: { ondismiss: () => setPaying(null) },
      });
      rz.open();
    } catch (e) { toast.error(apiErr(e)); } finally { setPaying(null); }
  };

  const buyOverage = async () => {
    if (!d.razorpay_configured) { toast.error("Online payments aren't enabled yet. Please contact PK AI."); return; }
    setBuyingOverage(true);
    try {
      const ok = await loadRazorpay();
      if (!ok) throw new Error("Could not load payment gateway");
      const { data: order } = await api.post("/billing/overage/create-order", {});
      const rz = new window.Razorpay({
        key: order.key_id, amount: order.amount, currency: order.currency, name: "PK AI Calling",
        description: `${order.calls} extra calls (pay-as-you-go)`, order_id: order.order_id,
        handler: async (resp) => {
          try { await api.post("/billing/overage/verify", resp); toast.success(`${order.calls} extra calls added!`); load(); }
          catch (e) { toast.error(apiErr(e)); }
        },
        theme: { color: "#F97316" },
        modal: { ondismiss: () => setBuyingOverage(false) },
      });
      rz.open();
    } catch (e) { toast.error(apiErr(e)); } finally { setBuyingOverage(false); }
  };

  if (d === null) return <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-indigo-500" /></div>;
  if (d === false) return <div className="text-sm text-slate-400">Could not load billing.</div>;
  const u = d.usage;

  return (
    <div>
      <PageHeader title="Billing & Plan" subtitle="Your subscription, usage and upgrades" />

      <div className="grid lg:grid-cols-3 gap-6 mb-6">
        <Card className="border shadow-none p-5 lg:col-span-1">
          <div className="text-xs text-slate-500">Current plan</div>
          <div className="text-2xl font-bold font-display mt-1" style={{ color: "#4f46e5" }}>{d.current_plan}</div>
          {u.over_limit && <div className="mt-3 text-xs text-red-600 flex items-start gap-1.5" data-testid="billing-over-limit"><AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />You've hit a plan limit — new calls are paused. Upgrade to resume.</div>}
          {!u.over_limit && u.near_limit && <div className="mt-3 text-xs text-orange-600 flex items-start gap-1.5" data-testid="billing-near-limit"><AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />You're close to a plan limit. Consider upgrading soon.</div>}
        </Card>
        <Card className="border shadow-none p-5 lg:col-span-2 space-y-4">
          <div className="flex items-center gap-2 text-sm font-semibold"><TrendingUp className="w-4 h-4 text-indigo-500" />This month's usage</div>
          <UsageBar label="Calls" used={u.calls_used} cap={u.calls_cap_effective || u.calls_cap} pct={u.calls_pct} />
          <UsageBar label="Leads" used={u.leads_used} cap={u.leads_cap} pct={u.leads_pct} />
          {u.overage_calls_purchased > 0 && <div className="text-xs text-emerald-600" data-testid="overage-purchased">+{u.overage_calls_purchased.toLocaleString("en-IN")} extra calls added via pay-as-you-go this month</div>}
        </Card>
      </div>

      {u.overage_available && (
        <Card className="border shadow-none p-5 mb-6 flex flex-col sm:flex-row sm:items-center gap-4" style={{ borderColor: "rgba(249,115,116,0.35)", background: "rgba(249,115,116,0.04)" }} data-testid="overage-card">
          <div className="flex-1">
            <div className="flex items-center gap-2 text-sm font-semibold"><Zap className="w-4 h-4 text-orange-500" />Pay-as-you-go top-up</div>
            <div className="text-xs text-slate-500 mt-1">
              {u.over_calls ? "You've used all your calls. " : "Running low? "}
              Add <b>{u.overage_batch.toLocaleString("en-IN")} extra calls</b> instantly for <b>{fmtINR(u.overage_amount_inr)}</b> (₹{u.overage_per_call_inr}/call). Charged immediately — no plan change.
            </div>
          </div>
          <Button onClick={buyOverage} disabled={buyingOverage || !d.razorpay_configured} style={{ background: "#F97316" }} data-testid="buy-overage-btn">
            {buyingOverage ? <Loader2 className="w-4 h-4 animate-spin" /> : `Top up ${u.overage_batch} calls`}
          </Button>
        </Card>
      )}

      <h3 className="font-semibold font-display mb-3">Plans</h3>
      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4">
        {d.plans.map((p) => (
          <Card key={p.id} className={`border shadow-none p-5 flex flex-col ${p.current ? "ring-2 ring-indigo-500" : ""}`} data-testid={`billing-plan-${p.name}`}>
            <div className="font-semibold font-display text-lg">{p.name}</div>
            <div className="text-2xl font-bold mt-1" style={{ color: "#4f46e5" }}>{fmtINR(p.price_monthly_inr)}<span className="text-sm text-slate-400 font-normal">/mo</span></div>
            <div className="text-xs text-slate-400 -mt-0.5">${p.price_monthly_usd || 0}/mo{p.overage_per_call_inr ? ` · ₹${p.overage_per_call_inr}/extra call` : ""}</div>
            <div className="text-xs text-slate-500 mt-2 mb-3">{p.max_calls_per_month ? `${p.max_calls_per_month.toLocaleString("en-IN")} calls/mo` : "Unlimited calls"} · {p.max_leads ? `${p.max_leads.toLocaleString("en-IN")} leads` : "Unlimited leads"}</div>
            <div className="text-xs space-y-1 flex-1">
              {FEATURES.map(([k, l]) => <div key={k} className="flex items-center gap-1.5">{p.features?.[k] ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <X className="w-3.5 h-3.5 text-slate-300" />}<span className={p.features?.[k] ? "" : "text-slate-300"}>{l}</span></div>)}
            </div>
            <div className="mt-4">
              {p.current ? <Button variant="outline" className="w-full" disabled>Current plan</Button> :
                <Button className="w-full" onClick={() => setUpgradePlan(p)} data-testid={`billing-upgrade-${p.name}`}>Upgrade</Button>}
            </div>
          </Card>
        ))}
      </div>
      {!d.razorpay_configured && !d.paypal_configured && <p className="text-xs text-slate-400 mt-4">Online payment isn't enabled yet. Contact PK AI to change your plan.</p>}
      <UpgradeDialog plan={upgradePlan} d={d} onClose={() => setUpgradePlan(null)} onDone={() => { setUpgradePlan(null); load(); }} onRazorpay={upgrade} paying={paying} />
    </div>
  );
}

function UpgradeDialog({ plan, d, onClose, onDone, onRazorpay, paying }) {
  return (
    <Dialog open={!!plan} onOpenChange={onClose}><DialogContent data-testid="upgrade-dialog">
      <DialogHeader><DialogTitle>Upgrade to {plan?.name}</DialogTitle></DialogHeader>
      {plan && (
        <div className="space-y-4">
          <div className="rounded-lg border p-4 bg-slate-50 dark:bg-white/5">
            <div className="text-2xl font-bold" style={{ color: "#4f46e5" }}>{fmtINR(plan.price_monthly_inr)}<span className="text-sm text-slate-400 font-normal"> / ${plan.price_monthly_usd || 0} per month</span></div>
            <div className="text-xs text-slate-500 mt-1">{plan.max_calls_per_month ? `${plan.max_calls_per_month.toLocaleString("en-IN")} calls/mo` : "Unlimited calls"} · {plan.max_leads ? `${plan.max_leads.toLocaleString("en-IN")} leads` : "Unlimited leads"}</div>
          </div>

          {d.razorpay_configured && (
            <Button className="w-full" onClick={() => onRazorpay(plan)} disabled={paying === plan.id} data-testid="pay-razorpay-btn">
              {paying === plan.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><IndianRupee className="w-4 h-4 mr-1" />Pay {fmtINR(plan.price_monthly_inr)} with Razorpay</>}
            </Button>
          )}

          {d.paypal_configured && plan.price_monthly_usd > 0 && (
            <div>
              {d.razorpay_configured && <div className="flex items-center gap-3 my-2 text-xs text-slate-400"><div className="flex-1 h-px bg-slate-200" />or pay in USD<div className="flex-1 h-px bg-slate-200" /></div>}
              <div data-testid="paypal-buttons">
                <PayPalScriptProvider options={{ "client-id": d.paypal_client_id, currency: "USD", intent: "capture" }}>
                  <PayPalButtons
                    style={{ layout: "horizontal", height: 40, tagline: false }}
                    forceReRender={[plan.id]}
                    createOrder={async () => { const r = await api.post("/billing/paypal/create-order", { plan_id: plan.id }); return r.data.order_id; }}
                    onApprove={async (data) => { try { await api.post("/billing/paypal/capture", { order_id: data.orderID, plan_id: plan.id }); toast.success(`Upgraded to ${plan.name}!`); onDone(); } catch (e) { toast.error(apiErr(e)); } }}
                    onError={() => toast.error("PayPal payment failed. Please try again.")}
                  />
                </PayPalScriptProvider>
              </div>
            </div>
          )}

          {!d.razorpay_configured && !d.paypal_configured && <p className="text-sm text-slate-500">Online payment isn't enabled yet. Please contact PK AI to change your plan.</p>}
        </div>
      )}
    </DialogContent></Dialog>
  );
}
